﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import * as React from "react";
import { createUseStyles } from "react-jss";
import { useIntl } from "~base/Internationalization/IntlContext";
import type { SoPNLConsilioTheme } from "~jrad/Styling/SoPNLConsilioTheme";
import type { SelfServiceLoginViewAction } from "../Store";
import {
    type SelfServiceLoginViewState,
    SelfServiceLoginRegistrationPage,
} from "../Types";
import { Button } from "~base/Components/Atoms/Button";
import classNames from "classnames";

//-------------------------------------------------------------------------------------
// Props
//-------------------------------------------------------------------------------------

export interface SelfServicePageNavigationControlsProps {
    state: SelfServiceLoginViewState;
    dispatch: React.Dispatch<SelfServiceLoginViewAction>;
    postRegistrationRequest: () => void;
    disablePrev?: boolean;
    disableNext?: boolean;
}

const useStyles = createUseStyles((theme: SoPNLConsilioTheme) => ({
    navigationContainer: {
        borderTop: "1px solid #777777",
        paddingTop: "20px",
        marginBottom: "20px",
    },
    navigationButtons: {
        display: "grid",

        gridAutoFlow: "column",
        gridAutoColumns: "max-content",
        justifyContent: "space-between",
    },
    navigationButton: {
        minWidth: "100px",
        fontSize: "16px",
        padding: "4px 12px",
    },
    navigationButtonPrev: {
        background: "#ffffff",
        color: theme.constants.primaryColor,
        border: "1px solid #cccccc",
    },
    navigationButtonNext: {},
    navigationButtonHidden: {
        visibility: "hidden",
    },
}));

//-------------------------------------------------------------------------------------
// Component
//-------------------------------------------------------------------------------------

const SelfServicePageNavigationControls: React.FunctionComponent<
    SelfServicePageNavigationControlsProps
> = ({
    state,
    dispatch,
    postRegistrationRequest,
    disablePrev = false,
    disableNext = false,
}) => {
    const intl = useIntl();
    const classes = useStyles();

    return (
        <div className={classes.navigationContainer}>
            <div className={classes.navigationButtons}>
                <Button
                    testIdentifier="PreviousPage"
                    disabled={!!disablePrev}
                    className={classNames(
                        classes.navigationButton,
                        classes.navigationButtonPrev,
                        (state.page ===
                            SelfServiceLoginRegistrationPage.TermsOfService ||
                            state.page ===
                                SelfServiceLoginRegistrationPage.AccessDenied) &&
                            classes.navigationButtonHidden
                    )}
                >
                    {intl.formatText(
                        "VisiFinance.UI.SelfServiceRegister.PreviousButton"
                    )}
                </Button>
                <Button
                    testIdentifier="NextPage"
                    disabled={!!disableNext}
                    onClick={() => {
                        postRegistrationRequest();
                    }}
                    className={classNames(
                        classes.navigationButton,
                        classes.navigationButtonNext,
                        state.page ===
                            SelfServiceLoginRegistrationPage.AccessDenied &&
                            classes.navigationButtonHidden
                    )}
                >
                    {intl.formatText(
                        "VisiFinance.UI.SelfServiceRegister.NextButton"
                    )}
                </Button>
            </div>
        </div>
    );
};

export default SelfServicePageNavigationControls;
